import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  TablePagination,
  IconButton,
  Checkbox,
  Tooltip,
  Grid,
  alpha,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from "@mui/icons-material";
import SearchBar from "../../pages/components/SearchBar";

// Reusable Toolbar Component
const ReusableToolbar = ({
  numSelected,
  onBulkDelete,
  onBulkEdit,
  viewStyle,
  setViewStyle,
  hasCardView = true,
  title = "",
  customActions = [],
}) => {
  const handleViewClick = () => {
    setViewStyle(viewStyle === "List" ? "Card" : "List");
  };

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "70px",
        minHeight: "70px", // Ensure consistent height
        borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
      }}
    >
      {/* Left side content with consistent alignment */}
      <Box
        display="flex"
        alignItems="center"
        minWidth="200px" // Prevent content jumping
        sx={{ flexShrink: 0 }}
      >
        {numSelected > 0 ? (
          <Typography
            color="inherit"
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              whiteSpace: "nowrap", // Prevent text wrapping
            }}
          >
            {numSelected} selected
          </Typography>
        ) : (
          title && (
            <Typography
              variant="h6"
              component="div"
              sx={{ whiteSpace: "nowrap" }}
            >
              {title}
            </Typography>
          )
        )}
      </Box>

      {/* Right side actions with consistent alignment */}
      <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        {numSelected > 0 ? (
          <>
            {numSelected === 1 && onBulkEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  size="medium"
                  onClick={onBulkEdit}
                  sx={{
                    minWidth: "40px",
                    height: "40px",
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {onBulkDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  size="medium"
                  onClick={onBulkDelete}
                  sx={{
                    minWidth: "40px",
                    height: "40px",
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            {customActions.map((action, index) => (
              <React.Fragment key={index}>{action}</React.Fragment>
            ))}
          </>
        ) : (
          <>
            {customActions.map((action, index) => (
              <React.Fragment key={index}>{action}</React.Fragment>
            ))}

            {hasCardView && (
              <Tooltip
                title={
                  viewStyle === "List"
                    ? "Change to Card View"
                    : "Change to List View"
                }
              >
                <IconButton
                  color="primary"
                  onClick={handleViewClick}
                  size="medium"
                  sx={{
                    borderRadius: "8px",
                    minWidth: "40px",
                    height: "40px",
                    p: 1,
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.08),
                    "&:hover": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                >
                  {viewStyle === "List" ? <ViewModuleIcon /> : <ViewListIcon />}
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </Toolbar>
  );
};

// Reusable Table List Component
const ReusableTableList = ({
  data,
  columns,
  selected,
  onSelectAllClick,
  onSelect,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  filteredCount,
  loading = false,
}) => {
  // Add selection column to the beginning
  const enhancedColumns = useMemo(() => {
    const selectColumn = {
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      headerClassName: "select-all-header",
      renderHeader: () => (
        <Checkbox
          indeterminate={selected.length > 0 && selected.length < filteredCount}
          checked={filteredCount > 0 && selected.length === filteredCount}
          onChange={onSelectAllClick}
          inputProps={{ "aria-label": "select all items" }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selected.includes(params.row.id)}
          onClick={(event) => onSelect(event, params.row.id)}
          inputProps={{ "aria-labelledby": `select-${params.row.id}` }}
        />
      ),
    };

    return [selectColumn, ...columns];
  }, [columns, selected, filteredCount, onSelectAllClick, onSelect]);

  return (
    <>
      <Box sx={{ height: "auto", width: "100%" }}>
        <DataGrid
          rows={data}
          columns={enhancedColumns}
          loading={loading}
          hideFooter={true}
          autoHeight
          disableColumnMenu
          disableSelectionOnClick
          checkboxSelection={false}
          rowHeight={70}
          headerHeight={56}
          getRowId={(row) => row.id}
          components={{
            Toolbar: () => null,
          }}
          sx={{
            boxShadow: 0,
            border: 0,
            "& .MuiDataGrid-cell": {
              padding: "8px 16px",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus": {
              outline: "none",
            },
          }}
        />
      </Box>

      {/* Pagination Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "8px 16px",
          borderTop: "1px solid rgba(224, 224, 224, 1)",
        }}
      >
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-toolbar": {
              alignItems: "center",
              minHeight: "40px",
              padding: "0 8px",
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                margin: 0,
                display: "flex",
                alignItems: "center",
              },
            ".MuiTablePagination-select": {
              marginRight: "8px",
              marginLeft: "8px",
            },
            ".MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Box>
    </>
  );
};

// Reusable Card View Component
const ReusableCardView = ({
  data,
  CardComponent,
  selected,
  onSelectAllClick,
  onSelect,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  filteredCount,
  cardProps = { xs: 12, sm: 6, md: 4, lg: 3 },
}) => {
  const isSelected = (id) => selected.indexOf(id) !== -1;

  return (
    <>
      <Box p={2}>
        {/* Select All for card view */}
        <Box px={2} py={1} display="flex" justifyContent="flex-end">
          <Checkbox
            indeterminate={
              selected.length > 0 && selected.length < filteredCount
            }
            checked={filteredCount > 0 && selected.length === filteredCount}
            onChange={onSelectAllClick}
          />
          <Typography
            variant="body2"
            style={{ marginLeft: "8px", alignSelf: "center" }}
          >
            Select All
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {data.map((item, index) => (
            <Grid item key={item.id} {...cardProps}>
              <CardComponent
                data={item}
                index={page * rowsPerPage + index}
                selected={isSelected(item.id)}
                onSelect={(event) => onSelect(event, item.id)}
                isSelected={isSelected}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Pagination Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "8px 16px",
          borderTop: "1px solid rgba(224, 224, 224, 1)",
        }}
      >
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-toolbar": {
              alignItems: "center",
              minHeight: "40px",
              padding: "0 8px",
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                margin: 0,
                display: "flex",
                alignItems: "center",
              },
            ".MuiTablePagination-select": {
              marginRight: "8px",
              marginLeft: "8px",
            },
            ".MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Box>
    </>
  );
};

// Main Reusable Table Component
const ReusableTable = ({
  // Data props
  data = [],
  columns = [],
  loading = false,

  // Search props
  searchQuery = "",
  onSearchChange = () => {},
  searchPlaceholder = "Search...",
  searchFields = [],

  // Selection props
  selected = [],
  onSelectAllClick = () => {},
  onSelect = () => {},

  // Pagination props
  page = 0,
  rowsPerPage = 10,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},

  // View props
  viewStyle = "List",
  setViewStyle = () => {},
  hasCardView = false,
  CardComponent = null,
  cardProps = { xs: 12, sm: 6, md: 4, lg: 3 },

  // Actions props
  onBulkEdit = null,
  onBulkDelete = null,
  customActions = [],

  // UI props
  title = "",
  showSearch = true,
}) => {
  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchQuery || !searchQuery.trim() || searchFields.length === 0)
      return data;

    return data.filter((row) => {
      const searchLower = searchQuery.toLowerCase().trim();
      return searchFields.some((field) => {
        const value = row[field];
        if (!value) return false;

        const fieldValue = value.toString().toLowerCase();
        const match = fieldValue.includes(searchLower);

        // Debug logging for specific searches
        if (searchLower === "a") {
          console.log(
            `Row "${
              row.name || row.id
            }": field "${field}" = "${fieldValue}", matches: ${match}`
          );
        }

        return match;
      });
    });
  }, [data, searchQuery, searchFields]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  // Add serial numbers to data
  const dataWithSerialNumbers = useMemo(() => {
    return paginatedData.map((item, index) => ({
      ...item,
      serialNumber: page * rowsPerPage + index + 1,
    }));
  }, [paginatedData, page, rowsPerPage]);

  return (
    <>
      {/* Search Bar */}
      {showSearch && (
        <SearchBar
          value={searchQuery}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          containerSx={{
            borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
          }}
        />
      )}

      {/* Main Table Paper */}
      <Paper>
        {/* Toolbar */}
        <ReusableToolbar
          numSelected={selected.length}
          onBulkDelete={onBulkDelete}
          onBulkEdit={onBulkEdit}
          viewStyle={viewStyle}
          setViewStyle={setViewStyle}
          hasCardView={hasCardView}
          title={title}
          customActions={customActions}
        />

        {/* Content - Toggle between List and Card views */}
        {viewStyle === "List" ? (
          <ReusableTableList
            data={dataWithSerialNumbers}
            columns={columns}
            selected={selected}
            onSelectAllClick={onSelectAllClick}
            onSelect={onSelect}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={onPageChange}
            handleChangeRowsPerPage={onRowsPerPageChange}
            filteredCount={filteredData.length}
            loading={loading}
          />
        ) : (
          hasCardView &&
          CardComponent && (
            <ReusableCardView
              data={dataWithSerialNumbers}
              CardComponent={CardComponent}
              selected={selected}
              onSelectAllClick={onSelectAllClick}
              onSelect={onSelect}
              page={page}
              rowsPerPage={rowsPerPage}
              handleChangePage={onPageChange}
              handleChangeRowsPerPage={onRowsPerPageChange}
              filteredCount={filteredData.length}
              cardProps={cardProps}
            />
          )
        )}
      </Paper>
    </>
  );
};

export default ReusableTable;
